import { useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api, orgID } from '@yingbaba/api';
import type { Milestone, ProjectMember, Task } from '@yingbaba/types';
import {
  Button,
  DatePicker,
  ErrorNotice,
  Field,
  Modal,
  Notice,
  Select,
  useConfirmLeave,
  useFormDraft,
  useToast,
} from '@yingbaba/ui';

type Props = {
  projectID: string;
  task?: Task;
  members: ProjectMember[];
  milestones: Milestone[];
  onSaved: (id: string) => void;
};

export function ProjectTaskEditor(props: Props) {
  const [editing, setEditing] = useState<Task | null>(null);
  const [generation, setGeneration] = useState(0);
  return (
    <>
      <Button
        size="sm"
        variant={props.task ? 'ghost' : 'primary'}
        aria-label={props.task ? `调整任务：${props.task.title}` : '添加任务'}
        onClick={() => {
          setEditing(
            props.task
              ? { ...props.task }
              : {
                  id: '',
                  created_at: '',
                  updated_at: '',
                  title: '',
                  description: '',
                  project_id: props.projectID,
                  milestone_id: '',
                  meeting_id: '',
                  assignee: '',
                  assignee_account_id: '',
                  priority: 'normal',
                  starts_at: '',
                  due_date: '',
                  position: 0,
                  status: 'pending',
                },
          );
          setGeneration((value) => value + 1);
        }}
      >
        {props.task ? '调整任务' : '添加任务'}
      </Button>
      {editing && (
        <Editor
          {...props}
          key={generation}
          task={editing}
          close={() => setEditing(null)}
          reload={(task) => {
            setEditing(task);
            setGeneration((value) => value + 1);
          }}
        />
      )}
    </>
  );
}

function Editor({
  projectID,
  task,
  members,
  milestones,
  onSaved,
  close,
  reload,
}: Omit<Props, 'task'> & { task: Task; close: () => void; reload: (task: Task) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [reloadOpen, setReloadOpen] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [conflict, setConflict] = useState(false);
  const attempt = useRef<{ key: string; body: string } | null>(null);
  const pending = useRef(false),
    alive = useRef(true);
  const organization = useRef(orgID());
  const qc = useQueryClient(),
    toast = useToast(),
    leave = useConfirmLeave(),
    id = useId();
  const controller = useRef<AbortController | null>(null);
  const current = () => alive.current && organization.current === orgID();
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
    };
  }, []);
  const draft = useFormDraft({
    enabled: true,
    busy,
    save: (form) => persist(form, true),
    discard: close,
  });
  const readonly = task.status === 'completed';
  const controlsDisabled = busy || readonly || uncertain;
  function requestClose() {
    if (!pending.current) leave(close);
  }
  async function persist(form: HTMLFormElement, leaving = false) {
    if (pending.current || readonly || (!uncertain && !form.reportValidity())) return false;
    const payload =
      uncertain && attempt.current
        ? JSON.parse(attempt.current.body)
        : Object.fromEntries(new FormData(form));
    if (
      payload.starts_at &&
      payload.due_date &&
      String(payload.starts_at) > String(payload.due_date)
    ) {
      setError(new Error('截止日期不能早于开始日期'));
      return false;
    }
    pending.current = true;
    setBusy(true);
    setError(null);
    controller.current = new AbortController();
    try {
      if (!attempt.current)
        attempt.current = {
          key: crypto.randomUUID(),
          body: JSON.stringify({
            ...payload,
            ...(task.id ? { expected_updated_at: task.updated_at } : {}),
          }),
        };
      const saved = await api<Task>(
        task.id ? `/projects/${projectID}/tasks/${task.id}` : `/projects/${projectID}/tasks`,
        {
          method: task.id ? 'PUT' : 'POST',
          signal: controller.current.signal,
          headers: task.id ? {} : { 'Idempotency-Key': attempt.current.key },
          body: attempt.current.body,
        },
      );
      if (!saved?.id) throw new Error('保存结果暂未确认，请重试核对');
      if (!current()) return false;
      // Commit the clean guard before changing the selected task in the URL.
      // A save requested by the leave dialog must let that dialog navigate.
      flushSync(() => draft.clean());
      void qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[1] === organization.current &&
          (String(query.queryKey[0]).startsWith('/workspace/') ||
            query.queryKey[0] === `/projects/${projectID}`),
      });
      toast(task.id ? '任务安排已更新' : '任务已添加');
      if (!leaving) onSaved(saved.id);
      close();
      return true;
    } catch (failure) {
      if (current()) {
        setError(failure);
        const unknown =
          !(failure instanceof ApiError) || failure.status >= 500 || failure.status === 408;
        setUncertain(unknown);
        setConflict(
          failure instanceof ApiError && ['TASK_CHANGED', 'TASK_COMPLETED'].includes(failure.code),
        );
        if (!unknown) attempt.current = null;
      }
      return false;
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  }
  async function reloadLatest() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    controller.current = new AbortController();
    try {
      const latest = await api<{ tasks: Task[] }>(`/projects/${projectID}`, {
        signal: controller.current.signal,
      });
      if (!current()) return;
      const found = latest.tasks.find((entry) => entry.id === task.id);
      if (!found) throw new Error('该任务已不可访问，当前输入仍保留，请关闭后核对项目权限。');
      draft.clean();
      reload(found);
    } catch (failure) {
      if (current()) {
        setError(failure);
        setReloadOpen(false);
      }
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  }
  const memberOptions = [
    { value: '', label: '未指定负责人' },
    ...members.map((member) => ({ value: member.account_id, label: member.name })),
  ];
  if (
    task.assignee_account_id &&
    !memberOptions.some((entry) => entry.value === task.assignee_account_id)
  )
    memberOptions.push({
      value: task.assignee_account_id,
      label: `${task.assignee || '原负责人'}（需重新选择）`,
    });
  return (
    <>
      <Modal
        title={task.id ? '调整任务安排' : '添加制作任务'}
        description={
          task.id
            ? '调整工作内容、负责人和时间，已有检查项与记录会保留。'
            : '把工作拆成具体任务，安排负责人、阶段和时间。'
        }
        open
        busy={busy}
        onOpenChange={(value) => {
          if (!value) requestClose();
        }}
        className="project-task-editor"
        footer={
          <>
            <Button variant="secondary" disabled={busy} onClick={requestClose}>
              取消
            </Button>
            <Button form={id} type="submit" loading={busy} disabled={readonly}>
              {uncertain ? '重试确认' : task.id ? '保存任务' : '添加任务'}
            </Button>
          </>
        }
      >
        {readonly && <Notice>该任务已完成。如需继续修改，请关闭弹窗并重新打开任务。</Notice>}
        <form
          id={id}
          ref={draft.ref}
          onChange={draft.onChange}
          className="form-stack"
          onSubmit={(event) => {
            event.preventDefault();
            void persist(event.currentTarget);
          }}
        >
          <fieldset disabled={controlsDisabled} className="form-draft-fields">
            <Field label="任务名称" htmlFor={`${id}-title`}>
              <input
                id={`${id}-title`}
                className="input"
                name="title"
                defaultValue={task.title}
                required
                minLength={2}
                maxLength={200}
              />
            </Field>
            <Field label="工作说明（可选）" htmlFor={`${id}-description`}>
              <textarea
                id={`${id}-description`}
                name="description"
                defaultValue={task.description}
                rows={3}
                maxLength={4000}
              />
            </Field>
            <div className="project-task-editor-grid">
              <Field label="负责人" htmlFor={`${id}-owner`} hint="只可分配给当前项目成员。">
                <Select
                  disabled={controlsDisabled}
                  id={`${id}-owner`}
                  name="assignee_account_id"
                  defaultValue={task.assignee_account_id || ''}
                  options={memberOptions}
                />
              </Field>
              <Field label="优先级" htmlFor={`${id}-priority`}>
                <Select
                  disabled={controlsDisabled}
                  id={`${id}-priority`}
                  name="priority"
                  defaultValue={task.priority || 'normal'}
                  options={[
                    { value: 'low', label: '低' },
                    { value: 'normal', label: '普通' },
                    { value: 'high', label: '高' },
                    { value: 'urgent', label: '紧急' },
                  ]}
                />
              </Field>
            </div>
            <Field label="所属阶段" htmlFor={`${id}-stage`}>
              <Select
                disabled={controlsDisabled}
                id={`${id}-stage`}
                name="milestone_id"
                defaultValue={task.milestone_id || ''}
                options={[
                  { value: '', label: '不关联阶段' },
                  ...milestones.map((stage) => ({ value: stage.id, label: stage.title })),
                ]}
              />
            </Field>
            <div className="project-task-editor-grid">
              <Field label="开始日期（可选）" htmlFor={`${id}-start`}>
                <DatePicker
                  disabled={controlsDisabled}
                  id={`${id}-start`}
                  name="starts_at"
                  defaultValue={task.starts_at}
                />
              </Field>
              <Field label="截止日期（可选）" htmlFor={`${id}-due`}>
                <DatePicker
                  disabled={controlsDisabled}
                  id={`${id}-due`}
                  name="due_date"
                  defaultValue={task.due_date}
                />
              </Field>
            </div>
          </fieldset>
          {uncertain && (
            <Notice>保存结果暂未确认，请重试核对。确认前会保留本次内容，避免重复添加任务。</Notice>
          )}
          <ErrorNotice error={error} />
          {conflict && (
            <div className="project-task-conflict">
              <p className="small muted">
                重新载入前可以继续查看当前输入；确认重新载入后，将以最新安排重新编辑。
              </p>
              <Button variant="secondary" disabled={busy} onClick={() => setReloadOpen(true)}>
                重新载入最新安排
              </Button>
            </div>
          )}
        </form>
      </Modal>
      <Modal
        title="重新载入任务安排？"
        description="将放弃当前未保存的修改，读取团队最新的任务安排。读取失败时会保留当前输入。"
        open={reloadOpen}
        busy={busy}
        onOpenChange={setReloadOpen}
        footer={
          <>
            <Button variant="secondary" disabled={busy} onClick={() => setReloadOpen(false)}>
              继续编辑
            </Button>
            <Button loading={busy} onClick={reloadLatest}>
              确认重新载入
            </Button>
          </>
        }
      >
        <p className="prose">{task.title}</p>
      </Modal>
    </>
  );
}
